/**
 * Pagina aanmaken of hernoemen.
 *
 * Het adres wordt live genormaliseerd en gecontroleerd tegen dezelfde regels
 * als de server: kleine letters, koppeltekens, geen geneste paden, geen
 * gereserveerde namen, en uniek binnen deze website. De gebruiker ziet
 * meteen wat het wordt in plaats van pas na een mislukte opslag.
 */
import React, { useEffect, useMemo, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { displayPath, normalizeSlug, slugFromTitle } from '../slug';
import type { SitePage } from '../types';

interface PageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `null` = nieuwe pagina. */
  page: SitePage | null;
  pages: SitePage[];
  onSubmit: (values: { title: string; slug: string; description: string | null }) => void;
}

export function PageDialog({ open, onOpenChange, page, pages, onSubmit }: PageDialogProps) {
  const isNew = page === null;
  const isHomepage = page?.slug === '';
  const willBeHomepage = isNew && !pages.some((item) => item.slug === '');

  const [title, setTitle] = useState('');
  const [slugInput, setSlugInput] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (!open) return;
    setTitle(page?.title ?? '');
    setSlugInput(page?.slug ?? '');
    setSlugTouched(false);
    setDescription(page?.description ?? '');
  }, [open, page]);

  const effectiveSlugInput = slugTouched || !isNew ? slugInput : slugFromTitle(title);

  const validation = useMemo(() => {
    if (isHomepage || willBeHomepage) return { ok: true as const, value: '', error: undefined };
    const check = normalizeSlug(effectiveSlugInput);
    if (!check.ok) return check;
    if (check.value === '') {
      return { ok: false as const, value: '', error: 'Vul een pagina-adres in.' };
    }
    const clash = pages.some(
      (item) => item.slug === check.value && item.pageId !== page?.pageId,
    );
    if (clash) {
      return { ok: false as const, value: '', error: 'Er is al een pagina met dit adres.' };
    }
    return check;
  }, [effectiveSlugInput, isHomepage, willBeHomepage, pages, page?.pageId]);

  const titleOk = title.trim().length > 0;
  const canSubmit = titleOk && validation.ok;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      title: title.trim(),
      slug: isHomepage || willBeHomepage ? '' : validation.value,
      description: description.trim() || null,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{isNew ? 'Nieuwe pagina' : 'Pagina-instellingen'}</DialogTitle>
            <DialogDescription>
              {willBeHomepage
                ? 'Dit wordt de homepage van je website.'
                : 'De titel staat in het tabblad van de browser en in zoekresultaten.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label htmlFor="sites-page-title" className="block text-sm font-semibold text-slate-800">
                Titel
              </label>
              <input
                id="sites-page-title"
                autoFocus
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Bijvoorbeeld: Over ons"
                className="mt-1.5 w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
              />
            </div>

            <div>
              <label htmlFor="sites-page-slug" className="block text-sm font-semibold text-slate-800">
                Adres
              </label>
              {isHomepage || willBeHomepage ? (
                <p className="mt-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-500">
                  De homepage staat altijd op <span className="font-semibold text-slate-700">/</span>
                </p>
              ) : (
                <>
                  <div className="mt-1.5 flex items-center rounded-xl border border-slate-300 px-3.5 transition focus-within:border-teal-500 focus-within:ring-2 focus-within:ring-teal-500/20">
                    <span className="select-none text-sm text-slate-400">/</span>
                    <input
                      id="sites-page-slug"
                      value={effectiveSlugInput}
                      onChange={(event) => {
                        setSlugTouched(true);
                        setSlugInput(event.target.value);
                      }}
                      placeholder="over-ons"
                      aria-invalid={!validation.ok}
                      className="w-full bg-transparent py-2.5 pl-0.5 text-sm outline-none"
                    />
                  </div>
                  {validation.error ? (
                    <p role="alert" className="mt-1.5 text-xs font-medium text-red-600">
                      {validation.error}
                    </p>
                  ) : (
                    <p className="mt-1.5 text-xs text-slate-500">
                      Bezoekers vinden deze pagina op{' '}
                      <span className="font-medium text-slate-700">{displayPath(validation.value)}</span>
                    </p>
                  )}
                </>
              )}
            </div>

            <div>
              <label
                htmlFor="sites-page-description"
                className="block text-sm font-semibold text-slate-800"
              >
                Korte omschrijving <span className="font-normal text-slate-400">(optioneel)</span>
              </label>
              <textarea
                id="sites-page-description"
                rows={2}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Wordt gebruikt in zoekresultaten."
                className="mt-1.5 w-full resize-none rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
              />
            </div>
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100"
            >
              Annuleren
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isNew ? 'Pagina maken' : 'Opslaan'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
